import { Container, Grid, Typography, Button, LinearProgress, Alert, AlertTitle, Box } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BasicUserData from './componets/BasicDataUser';
import BillingDetails from './componets/BillingDetails';
import ContactData from './componets/ContactData';
import { useEffect, useState } from 'react';
import { ContactDetails, dictionaryService, FormDataPerson, Token, sanitizeFormData } from './services/dictionaryService';
import Statements from './componets/StatementsData';
import SettlementData from './componets/SettlementData';
import Agreement from './componets/Agreement';
import { phoneRegex, getIbanError } from './helpers/validation';

// Sekcja oświadczeń ZUS (pola TAK/NIE) jest wyłączona — obecny proces jej nie wymaga.
// Kod zostaje w projekcie: ustawienie flagi na `true` przywraca sekcję razem z walidacją
// i wysyłką pola `declaration`. Rachunek bankowy jest od tego niezależny i zostaje zawsze.
const ZUS_STATEMENTS_ENABLED = false;

// Reguły walidacji pól z sekcji oświadczeń ZUS. Trzymane osobno i dopinane do schematu
// tylko gdy sekcja jest widoczna — inaczej formularz byłby nie do wysłania przez pola
// wymagane, których nikt nie widzi.
const zusStatementsValidation = {
    socialInsuranceFromOtherContract: Yup.boolean().required('To pole jest wymagane'),
    contractOfMandateWithOtherEntity: Yup.boolean().required('Pole jest wymagane'),
    salaryAboveMinimum: Yup.boolean().when('socialInsuranceFromOtherContract', {
        is: true,
        then: schema => schema.required('To pole jest wymagane'),
        otherwise: schema => schema.notRequired(),
    }),
    mandate_salaryAboveMinimum: Yup.boolean().when('contractOfMandateWithOtherEntity', {
        is: true,
        then: schema => schema.required('To pole jest wymagane'),
        otherwise: schema => schema.notRequired(),
    }),
    periodFrom: Yup.date()
        .nullable()
        .when('contractOfMandateWithOtherEntity', {
            is: true,
            then: schema => schema.required('Data rozpoczęcia jest wymagana'),
            otherwise: schema => schema.notRequired(),
        }),
    periodTo: Yup.date()
        .nullable()
        .when('contractOfMandateWithOtherEntity', {
            is: true,
            then: schema => schema.required('Data zakończenia jest wymagana'),
            otherwise: schema => schema.notRequired(),
        }),
    entityName: Yup.string()
        .max(100, 'Nazwa nie może mieć więcej niż 100 znaków')
        .when(['socialInsuranceFromOtherContract'], {
            is: (insurance: boolean) => insurance === true,
            then: schema => schema.required('Nazwa podmiotu jest wymagana'),
            otherwise: schema => schema.notRequired(),
        }),
    nameAndAddress: Yup.string()
        .max(100, 'Nazwa nie może mieć więcej niż 100 znaków')
        .when(['contractOfMandateWithOtherEntity'], {
            is: (insurance: boolean) => insurance === true,
            then: schema => schema.required('Nazwa podmiotu jest wymagana'),
            otherwise: schema => schema.notRequired(),
        }),
    retired: Yup.boolean().required('Pole jest wymagane'),
    pensioner: Yup.boolean().required('Pole jest wymagane'),
    student: Yup.boolean().required('Pole jest wymagane'),
    studentIdNumber: Yup.string().when('student', {
        is: true,
        then: schema => schema.required('Numer legitymacji jest wymagany'),
        otherwise: schema => schema.notRequired(),
    }),
    studentIdScan: Yup.mixed().when('student', {
        is: true,
        then: schema => schema.required('Skan legitymacji jest wymagany'),
        otherwise: schema => schema.notRequired(),
    }),
    disability: Yup.boolean().required('Pole jest wymagane'),
    degree: Yup.string().when('disability', {
        is: true,
        then: schema => schema.required('Stopień niepełnosprawności jest wymagany'),
        otherwise: schema => schema.notRequired(),
    }),
    voluntaryHealthInsurance: Yup.boolean().required('Pole jest wymagane'),
    businessActivity: Yup.boolean().required('Pole jest wymagane'),
};



export interface FormValues {
    firstName: string;
    lastName: string;
    email: string;
    pesel: string;
    dateOfBirth: Date | null;
    periodFrom: Date | null;
    periodTo: Date | null;
    taxOffice: string;
    taxOfficeDict: {
        value: string;
        label: string;
    };
    citizenship: string[];
    phoneNo: string;
    province: string; //WOJEWODZTWO
    district: string; //POWIAT
    commune: string; //GMINA
    city: string;
    postalCode: string;
    street: string;
    houseNumber: string;
    apartmentNumber: string;
    socialInsuranceFromOtherContract: boolean | undefined;
    contractOfMandateWithOtherEntity: boolean | undefined;
    nameAndAddress: string
    entityName: string;
    salaryAboveMinimum: boolean | undefined;
    mandate_salaryAboveMinimum: boolean | undefined;
    retired: boolean | undefined;
    pensioner: boolean | undefined;
    student: boolean | undefined;
    studentIdNumber: string;
    studentIdScan: File | null;
    disability: boolean | undefined;
    degree: string;
    voluntaryHealthInsurance: boolean | undefined,
    businessActivity: boolean | undefined,
    accountNumber: string
    bankName: string
    dataAgreement: boolean | undefined;
    personalDataProcessingConsent: boolean | undefined;
    privacyPolicyAcknowledgment: boolean | undefined;
}

const FormPagePerson = ({ tokenAll, contactDetails }: { tokenAll: Token, contactDetails: ContactDetails }) => {

    const [loading, setLoading] = useState(false);
    const [send, setSend] = useState(false);





    const formik = useFormik<FormValues>({
        initialValues: {
            dataAgreement: false,
            personalDataProcessingConsent: false,
            privacyPolicyAcknowledgment: false,
            firstName: '',
            lastName: '',
            email: '',
            phoneNo: "",
            pesel: "",
            dateOfBirth: null,
            periodFrom: null,
            periodTo: null,
            taxOffice: "",
            taxOfficeDict: {
              value: '',
              label: ''
            },
            citizenship: ['Polska'],
            province: "",
            district: "",
            commune: "",
            city: "",
            postalCode: "",
            street: "",
            houseNumber: "",
            apartmentNumber: "",
            contractOfMandateWithOtherEntity: undefined,
            socialInsuranceFromOtherContract: undefined,
            nameAndAddress: "",
            entityName: "",
            mandate_salaryAboveMinimum: undefined,
            salaryAboveMinimum: undefined,
            retired: undefined,
            pensioner: undefined,
            student: undefined,
            studentIdNumber: "",
            studentIdScan: null,
            disability: undefined,
            degree: "",
            voluntaryHealthInsurance: undefined,
            businessActivity: undefined,
            accountNumber: "",
            bankName: "",
        },
        validationSchema: Yup.object({
            ...(ZUS_STATEMENTS_ENABLED ? zusStatementsValidation : {}),
            province: Yup.string().required('Województwo jest wymagane'),
            district: Yup.string().required('Powiat jest wymagany'),
            commune: Yup.string().required('Gmina jest wymagana'),
            accountNumber: Yup.string()
                .required('Numer konta jest wymagany')
                // createError, żeby pokazać konkretną przyczynę zamiast jednego ogólnego komunikatu
                .test('is-valid-iban', '', function (value) {
                    const error = getIbanError(value ?? '');
                    return error ? this.createError({ message: error }) : true;
                }),
            pesel: Yup.string()
                .required('Pesel jest wymagany')
                .matches(/^\d{11}$/, 'Pesel musi składać się z 11 cyfr')
                .test('is-valid-pesel', 'Niepoprawny numer PESEL', value => {
                    if (!value) return false;
                    const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
                    const sum = value
                        .split('')
                        .slice(0, 10)
                        .reduce((acc, digit, index) => acc + parseInt(digit, 10) * weights[index], 0);
                    const controlDigit

                        = (10 - (sum % 10)) % 10;
                    return controlDigit === parseInt(value[10], 10);
                }),
            // dateOfBirth: Yup.date()
            //     .required('Data urodzenia jest wymagana')
            //     .test('is-consistent-with-pesel', 'Data urodzenia niezgodna z numerem PESEL', function (value) {
            //         const pesel = this.parent.pesel;
            //         if (!pesel || pesel.length !== 11 || !value) return true;

            //         const year = parseInt(pesel.substring(0, 2), 10);
            //         const month = parseInt(pesel.substring(2, 4), 10);
            //         const day = parseInt(pesel.substring(4, 6), 10);

            //         const fullYear = month > 20 ? 2000 + year : 1900 + year;
            //         const realMonth = (month > 20 ? month - 20 : month) - 1; // bo w JS miesiące 0–11

            //         const peselDate = new Date(fullYear, realMonth, day);

            //         return value.toDateString() === peselDate.toDateString();
            //     }),
            citizenship: Yup.array()
                .of(Yup.string())
                .min(1, 'Wybierz co najmniej jedno obywatelstwo')
                .required('Obywatelstwo jest wymagane'), taxOffice: Yup.string().required('Urząd skarbowy jest wymagany'),
            firstName: Yup.string().required('Imię jest wymagane'),
            lastName: Yup.string().required('Nazwisko jest wymagane'),
            email: Yup.string().email('Nieprawidłowy email').required('Email jest wymagany'),
            phoneNo: Yup.string()
                .required('Numer telefonu jest wymagany')
                .matches(phoneRegex, 'Niepoprawny numer telefonu (PL)'), street: Yup.string().required('Ulica jest wymagana'),
            postalCode: Yup.string()
                .required('Kod pocztowy jest wymagany')
                .matches(/^\d{2}-\d{3}$/, 'Kod pocztowy musi być w formacie 00-000'),
            city: Yup.string().required('Miasto jest wymagane'),
            houseNumber: Yup.string()
                .required('Numer domu jest wymagany')
                .max(6, 'Numer domu nie może być dłuższy niż 6 znaków'),
            dataAgreement: Yup.boolean().oneOf([true], 'To pole jest wymagane'),
            personalDataProcessingConsent: Yup.boolean().oneOf([true], 'To pole jest wymagane'),
            privacyPolicyAcknowledgment: Yup.boolean().oneOf([true], 'To pole jest wymagane'),
            apartmentNumber: Yup.string()
                .nullable()
                .max(6, 'Numer mieszkania nie może być dłuższy niż 6 znaków'),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            if (!tokenAll) return;
            const preparedData: FormDataPerson = {
                formType: "PERSONAL",
                status: "SUBMITTED",
                contactDetails: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    phoneNo: values.phoneNo,
                },
                token: tokenAll,
                personalData: {
                    lastName: values.lastName,
                    firstName: values.firstName,
                    middleName: "", // Assuming no middle name field in the form
                    pesel: values.pesel,
                    dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString().split('T')[0] : "",
                    citizenship: values.citizenship,
                    taxOfficeDict: values.taxOfficeDict,
                },
                residentialAddress: {
                    country: "Polska", // Assuming the country is Poland
                    voivodeship: values.province,
                    county: values.district,
                    municipality: values.commune,
                    street: values.street,
                    houseNumber: values.houseNumber,
                    apartmentNumber: values.apartmentNumber || undefined,
                    postalCode: values.postalCode,
                    city: values.city,
                },
                // Gdy sekcja jest wyłączona, nie wysyłamy pustego `declaration` z samymi
                // false — to byłyby oświadczenia, których twórca nigdy nie złożył.
                declaration: !ZUS_STATEMENTS_ENABLED ? undefined : {
                    socialInsuranceFromOtherContract: values.socialInsuranceFromOtherContract
                        ? {
                            entityName: values.entityName,
                            salaryAboveMinimum: values.salaryAboveMinimum || false,
                        }
                        : undefined,
                    contractOfMandateWithOtherEntity: values.contractOfMandateWithOtherEntity
                        ? {
                            nameAndAddress: values.nameAndAddress,
                            salaryAboveMinimum: values.mandate_salaryAboveMinimum || false,
                            periodFrom: values.periodFrom
                                ? new Date(values.periodFrom).toISOString()
                                : "",
                            periodTo: values.periodTo
                                ? new Date(values.periodTo).toISOString()
                                : "",
                        }
                        : undefined,
                    retired: values.retired || false,
                    pensioner: values.pensioner || false,
                    student: values.student || false,
                    disability: values.disability
                        ? {
                            haveDisability: true,
                            degree: values.degree as "lekki" | "umiarkowany" | "znaczny",
                        }
                        : undefined,
                    voluntaryHealthInsurance: values.voluntaryHealthInsurance || false,
                    businessActivity: values.businessActivity || false,
                },
                settlementData: {
                    accountNumber: values.accountNumber,
                    bankName: values.bankName,
                },
                declarationFooter: {
                    date: new Date().toISOString(),
                },
            };
            try {
                await dictionaryService.postFormPerson(tokenAll.value, sanitizeFormData(preparedData));
                setSend(true);
            } catch {
                alert('Wystąpił błąd podczas wysyłania formularza. Spróbuj ponownie.');
            }
            finally {
                setLoading(false);
            }
        },

    }

    );

    useEffect(() => {
        formik.setValues((prevValues) => ({
            ...prevValues,
            firstName: contactDetails.firstName,
            lastName: contactDetails.lastName,
            email: contactDetails.email,
            phoneNo: contactDetails.phoneNo,
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenAll, contactDetails])


    useEffect(() => {
        if (!formik.values.socialInsuranceFromOtherContract) {
            formik.setFieldValue('entityName', '', false);
            formik.setFieldValue('salaryAboveMinimum', null, false);
        }
        if (!formik.values.contractOfMandateWithOtherEntity) {
            formik.setFieldValue('nameAndAddress', '', false);
            formik.setFieldValue('periodFrom', null, false);
            formik.setFieldValue('periodTo', null, false);
            formik.setFieldValue('mandate_salaryAboveMinimum', null, false);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        formik.values.socialInsuranceFromOtherContract,
        formik.values.contractOfMandateWithOtherEntity
    ]);

    useEffect(() => {
        if (!formik.values.disability) {
            formik.setFieldValue('degree', '', false);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.disability]);

    return (
        <>
            <Container maxWidth="lg">
                {send ? (
                    <Alert severity="success" sx={{ marginTop: '160px' }}>
                        <AlertTitle>Formularz został pomyślnie wysłany!</AlertTitle>
                        Dziękujemy za przesłanie formularza.
                    </Alert>
                ) : (
                    <form onSubmit={formik.handleSubmit} >
                        <Box sx={{
                            mt: 10, mb: 4,
                            borderRadius: '8px',
                            padding: '24px',
                            fontSize: '24px',
                            fontFamily: '"Poppins", sans-serif', // Set font family to Poppins
                            backgroundColor: "#E0F2F1",
                            border: '1px solid #00796B',
                            marginTop: { xs: "120px", mt: "150px" },
                            paddingLeft:
                                { xs: "15px", lg: "24px" },
                            paddingRight: "12px"
                        }}>
                            <Typography variant="h5" gutterBottom>
                                Cześć,
                            </Typography>
                            <Typography variant="body1" gutterBottom >
                                już tylko krok dzieli Cię od dołączenia do grona partnerów{' '}
                                {!window.location.hostname.includes('brandlift') ? 'Sparky' : 'Brandlift'}
                                .  Formalności.
                                <br />
                                <br />
                                Nikt ich nie lubi, ale są konieczne. <span style={{ fontWeight: "600" }}>Poświęć 5 minut </span>i wypełnij uważnie
                                poniższy formularz. Na tej podstawie przygotujemy dokumenty,  które podeślemy Ci do zapoznania się i podpisu. Do dzieła!
                                <br />

                            </Typography>
                        </Box>
                        <BasicUserData<FormValues> formik={formik} />
                        <BillingDetails formik={formik} />
                        <ContactData formik={formik} />
                        {ZUS_STATEMENTS_ENABLED && (
                            <>
                                <Typography variant="h6" sx={{ marginTop: { xs: '80px', md: '130px' }, paddingBottom: { sx: '10px', md: '30px' } }}>
                                    Oświadczenie zleceniobiorcy niezbędne do ustalenia statusu ubezpieczeniowego ZUS
                                </Typography>
                                <Statements formik={formik} />
                            </>
                        )}
                        <Typography variant="h6" sx={{ marginTop: { xs: '80px', md: '130px' }, paddingBottom: { sx: '10px', md: '30px' } }}>
                            Rachunek bankowy
                        </Typography>
                        <SettlementData formik={formik} />
                        <Agreement formik={formik} />
                        {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
                            <Alert severity="error" sx={{ marginTop: 2 }}>
                                Proszę uzupełnić brakujące lub niepoprawne dane przed wysłaniem formularza.
                            </Alert>
                        )}
                        <Grid container justifyContent="center" sx={{ marginTop: Object.keys(formik.errors).length > 0 ? 5 : 10, marginBottom: 10, paddingBottom: '200px' }}>
                            {loading ? (
                                <LinearProgress sx={{ width: '100%' }} />
                            ) : (
                                <Button variant="contained" type="submit" color="primary" sx={{ backgroundColor: '#00796B', '&:hover': { backgroundColor: '#005A4F' } }}>
                                    Wyślij
                                </Button>
                            )}
                        </Grid>
                    </form>
                )}
            </Container>

        </>
    );
};

export default FormPagePerson;